import React, { useState } from 'react';
// Types for approval workflow
interface PendingAction {
  id: number;
  resource: HardwareResource;
  action: string;
  requestedBy: string;
  status: 'pending' | 'approved' | 'rejected';
}

// Types for Hardware Tool
interface HardwareZone {
  name: string;
  subzones: string[];
}

interface HardwareUnit {
  name: string;
  zones: HardwareZone[];
}

interface HardwareSubzone {
  unit: string;
  zone: string;
  subzone: string;
}

interface HardwareResource {
  type: 'server' | 'disk';
  name: string;
}

/**
 * HardwareTool - Hardware Inventory Management
 * Azure-style inventory view by Business Unit → Zone → Resources
 */
export const HardwareTool: React.FC = () => {
    // Approval workflow state
    const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
    const [actionIdCounter, setActionIdCounter] = useState(1);
    // Simulate current user (in real app, get from auth)
    const currentUser = 'UserA';
  const isLocalhost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

  // Hardware tree structure
  const hardwareTree: HardwareUnit[] = [
    {
      name: 'North America Operations',
      zones: [
        { name: 'EAZ', subzones: ['Plant A', 'Plant B'] },
        { name: 'DMZ', subzones: ['DMZ Core', 'DMZ Edge'] },
        { name: 'PCN', subzones: ['PCN East', 'PCN West'] },
      ],
    },
    {
      name: 'Australian Business Unit',
      zones: [
        { name: 'PCN', subzones: ['Sydney DC', 'Perth DC'] },
        { name: 'EAZ', subzones: ['EAZ Alpha', 'EAZ Beta'] },
        { name: 'DMZ', subzones: ['DMZ Core'] },
      ],
    },
    {
      name: 'Refineries',
      zones: [
        { name: 'EAZ', subzones: ['Refinery North', 'Refinery South'] },
      ],
    },
    {
      name: 'Global',
      zones: [
        { name: 'DMZ', subzones: ['Global Hub'] },
      ],
    },
  ];

  // State for expanded nodes
  const [hardwareExpanded, setHardwareExpanded] = useState<Record<string, boolean>>({
    'bu:North America Operations': true,
    'zone:North America Operations/EAZ': true,
    'zone:North America Operations/DMZ': false,
    'zone:North America Operations/PCN': false,
    'bu:Australian Business Unit': true,
    'zone:Australian Business Unit/PCN': true,
    'zone:Australian Business Unit/EAZ': false,
    'zone:Australian Business Unit/DMZ': false,
    'bu:Refineries': false,
    'bu:Global': false,
  });

  // State for selected subzone
  const [selectedHardwareSubzone, setSelectedHardwareSubzone] = useState<HardwareSubzone | null>({
    unit: 'Australian Business Unit',
    zone: 'PCN',
    subzone: 'Sydney DC',
  });

  // State for selected resource (server or disk)
  const [selectedHardwareResource, setSelectedHardwareResource] = useState<HardwareResource | null>(null);
  // Handler to request an action (requires approval)
  const requestAction = (resource: HardwareResource, action: string) => {
    setPendingActions((prev) => [
      ...prev,
      {
        id: actionIdCounter,
        resource,
        action,
        requestedBy: currentUser,
        status: 'pending',
      },
    ]);
    setActionIdCounter((id) => id + 1);
  };

  // Handler to approve/reject an action
  const updateActionStatus = (id: number, status: 'approved' | 'rejected') => {
    setPendingActions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, status } : a))
    );
  };

  // Generate dummy resources based on subzone name
  const getHardwareResources = (subzone: string) => {
    const slug = subzone.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || 'ZONE';
    const servers = Array.from({ length: 3 }, (_, index) => `${slug}-SRV-0${index + 1}`);
    const disks = Array.from({ length: 2 }, (_, index) => `${slug}-DSK-0${index + 1}`);
    return { servers, disks };
  };

  // Toggle tree node expansion
  const toggleHardwareNode = (key: string) => {
    setHardwareExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex-1 overflow-auto bg-gradient-to-br from-[#F8F9FA] via-white to-[#E6F0F9]">
      <div className="max-w-7xl mx-auto p-8 relative">
        {/* Header */}
        <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-[#0054A4] mb-2">Hardware</h2>
            <p className="text-gray-600">Azure-style inventory view by Business Unit → Zone → Resources</p>
          </div>
          <div className="flex items-center gap-2">
            {isLocalhost && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-3 py-1 rounded-full">Draft</span>
            )}
            <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Dummy data</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Tree / Directory Panel */}
          <div className="lg:col-span-4 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Business Units</h3>
              <button className="text-xs font-medium text-[#0054A4] hover:text-[#003A73]">New Group</button>
            </div>

            <div className="text-sm text-gray-700 space-y-3">
              {hardwareTree.map((unit) => {
                const unitKey = `bu:${unit.name}`;
                const unitExpanded = hardwareExpanded[unitKey] ?? false;

                return (
                  <div key={unit.name}>
                    <div className="flex items-center gap-2 font-semibold text-[#1E3A5F]">
                      {unit.zones.length > 0 ? (
                        <button
                          className="text-gray-500 w-4 text-left"
                          onClick={() => toggleHardwareNode(unitKey)}
                          aria-label={`${unitExpanded ? 'Collapse' : 'Expand'} ${unit.name}`}
                        >
                          {unitExpanded ? '▾' : '▸'}
                        </button>
                      ) : (
                        <span className="w-4"></span>
                      )}
                      <span>{unit.name}</span>
                    </div>

                    {unitExpanded && unit.zones.length > 0 && (
                      <div className="ml-4 mt-2 border-l border-gray-200 pl-3 space-y-2">
                        {unit.zones.map((zone) => {
                          const zoneKey = `zone:${unit.name}/${zone.name}`;
                          const zoneExpanded = hardwareExpanded[zoneKey] ?? false;

                          return (
                            <div key={`${unit.name}-${zone.name}`}>
                              <div className="flex items-center gap-2 text-gray-700">
                                <button
                                  className="text-gray-400 w-4 text-left"
                                  onClick={() => toggleHardwareNode(zoneKey)}
                                  aria-label={`${zoneExpanded ? 'Collapse' : 'Expand'} ${zone.name}`}
                                >
                                  {zoneExpanded ? '▾' : '▸'}
                                </button>
                                <span>{zone.name}</span>
                              </div>

                              {zoneExpanded && (
                                <div className="ml-4 mt-1 border-l border-gray-200 pl-3 space-y-1">
                                  {zone.subzones.map((subzone) => {
                                    const isSelected =
                                      selectedHardwareSubzone?.unit === unit.name &&
                                      selectedHardwareSubzone?.zone === zone.name &&
                                      selectedHardwareSubzone?.subzone === subzone;

                                    return (
                                      <button
                                        key={`${unit.name}-${zone.name}-${subzone}`}
                                        className={`w-full text-left px-2 py-1 rounded-md transition-colors ${
                                          isSelected
                                            ? 'bg-blue-50 text-[#0054A4] font-semibold'
                                            : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                        onClick={() => {
                                          setSelectedHardwareSubzone({
                                            unit: unit.name,
                                            zone: zone.name,
                                            subzone,
                                          });
                                          setSelectedHardwareResource(null);
                                        }}
                                      >
                                        {subzone}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Details Panel */}
          <div className="lg:col-span-8 space-y-6">
            {/* Selected Group Summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedHardwareSubzone
                      ? `${selectedHardwareSubzone.subzone} • ${selectedHardwareSubzone.zone} • ${selectedHardwareSubzone.unit}`
                      : 'Select a subzone'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedHardwareSubzone
                      ? 'Selected subzone with servers and disks.'
                      : 'Choose a subzone from the tree to view resources.'}
                  </p>
                </div>
                <div className="text-xs text-gray-500">Updated 2m ago</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Servers</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedHardwareSubzone ? getHardwareResources(selectedHardwareSubzone.subzone).servers.length : 0}
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Disks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedHardwareSubzone ? getHardwareResources(selectedHardwareSubzone.subzone).disks.length : 0}
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Backups</p>
                  <p className="text-2xl font-bold text-gray-900">8</p>
                </div>
              </div>
            </div>

            {/* Resource & Actions */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              <div className="xl:col-span-3 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Resources</h4>
                  <button className="text-xs font-medium text-[#0054A4] hover:text-[#003A73]">Add resource</button>
                </div>

                {!selectedHardwareSubzone && (
                  <div className="text-sm text-gray-500">Select a subzone to see servers and disks.</div>
                )}

                {selectedHardwareSubzone && (() => {
                  const resources = getHardwareResources(selectedHardwareSubzone.subzone);

                  return (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Servers</p>
                        <div className="space-y-2">
                          {resources.servers.map((serverName) => {
                            const isSelected = selectedHardwareResource?.type === 'server' && selectedHardwareResource?.name === serverName;

                            return (
                              <button
                                key={serverName}
                                className={`w-full text-left p-3 border rounded-lg transition-colors ${
                                  isSelected
                                    ? 'border-blue-300 bg-blue-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                                onClick={() => setSelectedHardwareResource({ type: 'server', name: serverName })}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">{serverName}</p>
                                    <p className="text-xs text-gray-500">Windows Server • 10.20.4.12</p>
                                  </div>
                                  <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-1 rounded">Healthy</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 mb-2">Disks</p>
                        <div className="space-y-2">
                          {resources.disks.map((diskName) => {
                            const isSelected = selectedHardwareResource?.type === 'disk' && selectedHardwareResource?.name === diskName;

                            return (
                              <button
                                key={diskName}
                                className={`w-full text-left p-3 border rounded-lg transition-colors ${
                                  isSelected
                                    ? 'border-amber-300 bg-amber-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                                onClick={() => setSelectedHardwareResource({ type: 'disk', name: diskName })}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">{diskName}</p>
                                    <p className="text-xs text-gray-500">Managed Disk • 2 TB</p>
                                  </div>
                                  <span className="text-xs font-semibold bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Warning</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="xl:col-span-2 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Actions</h4>
                {!selectedHardwareResource && (
                  <p className="text-sm text-gray-500">Select a server or disk to enable actions.</p>
                )}

                {selectedHardwareResource?.type === 'server' && (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-500">Selected: {selectedHardwareResource.name}</div>
                    <button
                      className="w-full bg-[#0054A4] hover:bg-[#003A73] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Restart Server')}
                    >
                      Request Restart Server
                    </button>
                    <button
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold py-2.5 rounded-lg border border-gray-300 transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Create Backup')}
                    >
                      Request Create Backup
                    </button>
                    <button
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold py-2.5 rounded-lg border border-gray-300 transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Attach Disk')}
                    >
                      Request Attach Disk
                    </button>
                  </div>
                )}

                {selectedHardwareResource?.type === 'disk' && (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-500">Selected: {selectedHardwareResource.name}</div>
                    <button
                      className="w-full bg-[#0054A4] hover:bg-[#003A73] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Resize Disk')}
                    >
                      Request Resize Disk
                    </button>
                    <button
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold py-2.5 rounded-lg border border-gray-300 transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Restart Disk')}
                    >
                      Request Restart Disk
                    </button>
                    <button
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold py-2.5 rounded-lg border border-gray-300 transition-colors"
                      onClick={() => requestAction(selectedHardwareResource, 'Create Snapshot')}
                    >
                      Request Create Snapshot
                    </button>
                  </div>
                )}

                {/* Pending Approvals Section */}
                <div className="mt-6">
                  <p className="text-xs text-gray-500 mb-2">Pending Approvals</p>
                  <div className="space-y-2 text-xs text-gray-600">
                    {pendingActions.length === 0 && <div>No pending actions.</div>}
                    {pendingActions.map((action) => (
                      <div key={action.id} className="flex flex-col md:flex-row md:items-center md:justify-between border rounded p-2 mb-1 bg-gray-50">
                        <div>
                          <span className="font-semibold">{action.action}</span> on <span className="font-mono">{action.resource.name}</span> [{action.resource.type}]<br/>
                          <span className="text-gray-400">Requested by: {action.requestedBy}</span>
                        </div>
                        <div className="flex gap-2 mt-2 md:mt-0">
                          {action.status === 'pending' && (
                            <>
                              <button
                                className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold hover:bg-green-200"
                                onClick={() => updateActionStatus(action.id, 'approved')}
                              >Approve</button>
                              <button
                                className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-semibold hover:bg-red-200"
                                onClick={() => updateActionStatus(action.id, 'rejected')}
                              >Reject</button>
                            </>
                          )}
                          {action.status === 'approved' && <span className="text-green-700 font-semibold">Approved</span>}
                          {action.status === 'rejected' && <span className="text-red-700 font-semibold">Rejected</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardwareTool;
